import { Box, Grid, Stack, useMediaQuery, useTheme } from '@mui/material';
import React, { ReactElement, useContext, useMemo } from 'react';
import {
    AppFormikTextField,
    AppPrimaryButton,
} from 'shared/components/AppComponents';
import CompletedModal, { inferCompletedModal } from './CompletedModal';
import { FixPacketMeta, FormikPropsFixPacketValues } from '../FixPacketPage';
import { useFormikContext } from 'formik';
import mergeRefs from 'merge-refs';
import { useLocation, useNavigate } from 'react-router-dom';
import ReworkBulkModal, { inferDocumentReworkModal } from './BulkReworkModal';
import { RawData } from '../../listing-packets/components/VirtualizedTable';
import { Toast } from 'shared/helpers';
import { useNetworkState } from 'react-use';

const Footer: React.FC<SinglePacketFooter> = (props): ReactElement => {
    const {
        onSave,
        onSubmit,
        onBack,
        onFailValidation,
        isLoading,
        innerModalCompletedRef,
        unexpectedType,
        updateGroup,
        onModalChanged,
        isActiveReworks,
    } = props;
    const { validateForm, setFieldTouched, setFieldValue, values } =
        useFormikContext<FormikPropsFixPacketValues>();
    const navigate = useNavigate();
    const location = useLocation();
    const theme = useTheme();

    const match = useMediaQuery(theme.breakpoints.up('md'));
    const defaultState = location.state as RawData;
    const currentIdx = useMemo(
        () =>
            window.history.state &&
            typeof window.history.state.idx !== 'undefined'
                ? window.history.state.idx
                : null,
        []
    );
    const isPrimaryRoute = useMemo(
        () => typeof currentIdx === 'number' && currentIdx === 0,
        [currentIdx]
    );
    const clientId = useMemo(
        () =>
            defaultState && defaultState.clientId ? defaultState.clientId : '',
        [defaultState]
    );
    /** @constant {string} - clientId from action uuid in packet data */
    const packetId = useMemo(
        () =>
            defaultState && defaultState.actions && defaultState.actions.uuid
                ? defaultState.actions.uuid
                : '',
        [defaultState]
    );
    const isStateAvailable = useMemo(
        () => typeof location.state === 'object' && location.state !== null,
        [location.state]
    );

    const FixPacketMetaProps = useContext(FixPacketMeta);
    const completedModalRef = React.useRef<inferCompletedModal | null>(null);
    const innerModalDocumentBulkReworkRef =
        React.useRef<inferDocumentReworkModal | null>(null);
    /**
     * handle action save to open confirmation modal
     */
    const actionOnSaveChangesToOpenModal = React.useCallback(async () => {
        if (typeof onFailValidation === 'function') onFailValidation();
        const validate = await validateForm()
            .then((response) => {
                if (response && Object.keys(response).includes('documents')) {
                    if (
                        Array.isArray(response.documents) &&
                        response.documents.length > 0
                    ) {
                        if (updateGroup) {
                            response.documents.forEach((el, index) => {
                                setFieldTouched(
                                    `documents[${index}].groups`,
                                    true
                                );
                            });
                        } else {
                            response.documents.forEach((el, index) => {
                                setFieldTouched(
                                    `documents[${index}].category`,
                                    true
                                );
                            });
                        }
                    }
                    return false;
                }

                return true;
            })
            .catch(() => false);
        if (validate && typeof onSubmit === 'function') onSubmit();
    }, [
        onFailValidation,
        onSubmit,
        setFieldTouched,
        updateGroup,
        validateForm,
    ]);
    const handleOnSubmitType = React.useCallback(() => {
        if (unexpectedType) {
            if (typeof onSubmit === 'function') {
                onSubmit();
            }
        } else {
            actionOnSaveChangesToOpenModal();
        }
    }, [actionOnSaveChangesToOpenModal, onSubmit, unexpectedType]);

    const { online } = useNetworkState();
    const handleSendToRework = React.useCallback(async () => {
        if (online === false) {
            Toast.showErrorMessage('No internet connection, Please try again!');
            return;
        }
        if (typeof onFailValidation === 'function') onFailValidation();
        const validate = await validateForm()
            .then((response) => {
                if (response && Object.keys(response).includes('documents')) {
                    if (
                        Array.isArray(response.documents) &&
                        response.documents.length > 0 &&
                        response.documents.some(
                            (t: any) =>
                                typeof t === 'object' &&
                                Object.keys(t).filter(
                                    (d) =>
                                        d !== 'predefinedMessage' &&
                                        d !== 'statusMessage'
                                ).length > 0
                        )
                    ) {
                        if (updateGroup) {
                            response.documents.forEach((el, index) => {
                                setFieldTouched(
                                    `documents[${index}].groups`,
                                    true
                                );
                            });
                        } else {
                            response.documents.forEach((el, index) => {
                                setFieldTouched(
                                    `documents[${index}].category`,
                                    true
                                );
                            });
                        }
                        return false;
                    }
                    return true;
                }

                return true;
            })
            .catch(() => false);
        if (validate) innerModalDocumentBulkReworkRef.current?.setOpen();
    }, [onFailValidation, online, setFieldTouched, updateGroup, validateForm]);

    const handleBackWithinAvailableState = React.useCallback(() => {
        if (isStateAvailable && typeof onBack === 'function') {
            onBack();
        } else {
            FixPacketMetaProps?.setActiveBackNavigation(true);
            if (isPrimaryRoute) {
                navigate('/manage-packets', { replace: true });
            } else {
                navigate(-1);
            }
        }
    }, [
        FixPacketMetaProps,
        isPrimaryRoute,
        isStateAvailable,
        navigate,
        onBack,
    ]);
    const handleFocusButtonSendRework = React.useCallback(() => {
        setFieldValue('isOpenToSendRework', true);
    }, [setFieldValue]);
    const handleBlurButtonSendRework = React.useCallback(() => {
        setFieldValue('isOpenToSendRework', false);
    }, [setFieldValue]);
    return (
        <Box className="footer fixpacket-footer">
            <Stack
                direction={match ? 'row' : 'column'}
                display="flex"
                flexGrow={1}
            >
                <>
                    <AppFormikTextField
                        name="isOpenToSendRework"
                        inputProps={{
                            type: 'hidden',
                        }}
                        className="isOpenToSendRework"
                    />
                    <AppPrimaryButton
                        loadingSize={22}
                        loadingText="PROCESSING"
                        title="Send To Rework"
                        disabled={isActiveReworks === false}
                        className="btn-small save-button"
                        onFocus={handleFocusButtonSendRework}
                        onBlur={handleBlurButtonSendRework}
                        onClick={handleSendToRework}
                    />
                </>

                <Box className="saving-fixpacket-singlepage">
                    <Grid
                        container
                        spacing={2}
                        className="categorise"
                        direction="row"
                    >
                        <Grid item xs={6} className="categorise-item">
                            <AppPrimaryButton
                                disabled={
                                    FixPacketMetaProps?.isActiveBackNavigation
                                }
                                onClick={handleBackWithinAvailableState}
                                title="Back"
                                className="btn-small back-button"
                            />
                        </Grid>

                        <Grid item xs={6} className="categorise-item">
                            <AppPrimaryButton
                                isLoading={isLoading}
                                loadingSize={22}
                                disabled={
                                    isLoading ||
                                    isActiveReworks ||
                                    isStateAvailable === false
                                }
                                loadingText="PROCESSING"
                                title="Save changes"
                                className="btn-small save-button"
                                onClick={handleOnSubmitType}
                            />
                        </Grid>
                    </Grid>
                </Box>
            </Stack>
            <ReworkBulkModal
                datepickerRework={values.datepickerRework}
                missingDocuments={values.missingDocuments}
                documents={values.documents}
                clientId={clientId}
                packetId={packetId}
                ref={innerModalDocumentBulkReworkRef}
                callbackOpenModal={() => {
                    // setReworkModal(true);
                    // // console.log('test');
                    if (typeof onModalChanged === 'function')
                        onModalChanged(true, false, false);
                }}
                callbackClosingModal={() => {
                    // setReworkModal(true);
                    // // console.log('test');
                    if (typeof onModalChanged === 'function')
                        onModalChanged(false, false, false);
                }}
                callbackSavingModal={() => {
                    // setReworkModal(true);
                    // // console.log('test');
                    if (typeof onModalChanged === 'function')
                        onModalChanged(false, true, true);
                }}
            />
            <CompletedModal
                ref={mergeRefs(innerModalCompletedRef, completedModalRef)}
                onHandleSubmit={onSave}
            />
        </Box>
    );
};

export default Footer;
